import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
} from '@mui/material';
import useGlobal from 'global-state/store';
import useMenu from 'menu-actions/useMenu';
import { useTranslation } from 'react-i18next';
import { typeFromValue } from '../utils/containerType';
import { useOperationData } from './hooks/useOperationData';
import { useOperation } from './hooks/useOperation';
import OperationContainer from './components/OperationContainer';
import OfflineIndicator from './components/OfflineIndicator';
import InventoryStorageGuard from './components/InventoryStorageGuard';
import ReportIncidentDialog from './ReportIncidentDialog';
import {
  buildContainerSearchOptions,
  findContainerMatch,
  getOperationContainerPath,
} from './utils/containerSearch';

export default function Operation() {
  const location = useLocation();
  const {
    containerId, inventoryData, previousItemFormValues,
  } = location.state || {};
  const navigate = useNavigate();

  useEffect(() => {
    if (containerId === undefined || !inventoryData) {
      navigate('/');
    }
  }, [containerId, inventoryData, navigate]);

  if (containerId === undefined || !inventoryData) {
    return <CircularProgress />;
  }

  return (
    <InventoryStorageGuard>
      <OperationWithLocation
        key={containerId}
        inventoryData={inventoryData}
        containerId={containerId}
        previousItemFormValues={previousItemFormValues}
      />
    </InventoryStorageGuard>
  );
}

function OperationWithLocation({ inventoryData, previousItemFormValues, containerId }) {
  const navigate = useNavigate();
  const [globalState, globalActions] = useGlobal();
  const { dispatchMenuActions } = useMenu();
  const { t } = useTranslation();
  const [openReportIncident, setOpenReportIncident] = useState(false);

  // Custom hooks for data management
  const {
    currentInventoryData,
    setCurrentInventoryData,
    currentContainerData,
    currentContainer,
    getContainerId,
  } = useOperationData(inventoryData, containerId);

  // Determine container type
  const containerType = currentContainerData
    && currentInventoryData.mappingConfig.columns.type?.mapping?.csvName
    ? typeFromValue(
      currentContainerData[currentInventoryData.mappingConfig.columns.type.mapping.csvName],
    )
    : undefined;

  // Main operation hook with recording and navigation
  const { recordOperation, nextOperation } = useOperation(
    globalState,
    currentInventoryData,
    setCurrentInventoryData,
    currentContainerData,
    getContainerId,
    currentContainer,
    containerType,
    t,
    globalActions,
    navigate,
  );

  const reportIncidentAction = useCallback(() => ({
    name: t('operation.report_incident'),
    callback: () => setOpenReportIncident(true),
  }), [t]);

  useEffect(() => {
    dispatchMenuActions({ type: 'add', actions: [reportIncidentAction()] });
  }, [dispatchMenuActions, reportIncidentAction]);

  // Handle navigation after operation recording
  const handleOperationComplete = useCallback(async (values, processToNext) => {
    const result = await recordOperation(values, processToNext);
    if (result.shouldNavigateToNext) {
      nextOperation(result.updatedData, result.values);
    }
  }, [recordOperation, nextOperation]);

  const navigateToContainer = useCallback((targetContainer) => {
    if (targetContainer && targetContainer.id !== containerId) {
      navigate(getOperationContainerPath(
        targetContainer,
        currentInventoryData.mappingConfig.columns.ivtContainerId.mapping.csvName,
      ), {
        state: {
          containerId: targetContainer.id,
          inventoryData: currentInventoryData,
          previousItemFormValues,
        },
        replace: true,
      });
    }
  }, [
    containerId,
    currentInventoryData,
    navigate,
    previousItemFormValues,
  ]);

  const handleSearchContainer = useCallback((searchValue) => {
    const match = findContainerMatch(
      currentInventoryData.containersArray,
      currentInventoryData.mappingConfig,
      searchValue,
    );

    if (match.status === 'matched') {
      navigateToContainer(match.container);
      return;
    }

    const message = match.status === 'multiple'
      ? t('operation.container_search_multiple_matches')
      : t('operation.container_not_found');

    globalActions.setSnackbarMessage({
      message,
      severity: 'warning',
    });
  }, [
    currentInventoryData,
    globalActions,
    t,
    navigateToContainer,
  ]);

  const handleSelectContainer = useCallback((selectedOption) => {
    navigateToContainer(selectedOption?.container);
  }, [navigateToContainer]);

  const currentContainerIndex = useMemo(() => currentInventoryData.containersArray.findIndex(
    (container) => container.id === containerId,
  ), [containerId, currentInventoryData]);

  const previousContainer = currentContainerIndex > 0
    ? currentInventoryData.containersArray[currentContainerIndex - 1]
    : null;
  const nextContainerData = currentContainerIndex >= 0
    && currentContainerIndex < currentInventoryData.containersArray.length - 1
    ? currentInventoryData.containersArray[currentContainerIndex + 1]
    : null;

  const handlePreviousContainer = useCallback(() => {
    navigateToContainer(previousContainer);
  }, [navigateToContainer, previousContainer]);

  const handleNextContainer = useCallback(() => {
    navigateToContainer(nextContainerData);
  }, [navigateToContainer, nextContainerData]);

  const containerSearchOptions = useMemo(() => buildContainerSearchOptions(
    currentInventoryData.containersArray,
    currentInventoryData.mappingConfig,
    containerId,
  ), [containerId, currentInventoryData]);

  if (currentInventoryData && currentContainerData) {
    return (
      <Box sx={{ mt: 1 }}>
        <OfflineIndicator
          networkState={globalState.networkState}
        />
        <OperationContainer
          currentContainerData={currentContainerData}
          currentInventoryData={currentInventoryData}
          setCurrentInventoryData={setCurrentInventoryData}
          recordOperation={handleOperationComplete}
          nextOperation={nextOperation}
          canGoPrevious={Boolean(previousContainer)}
          canGoNext={Boolean(nextContainerData)}
          onPreviousContainer={handlePreviousContainer}
          onNextContainer={handleNextContainer}
          containerSearchOptions={containerSearchOptions}
          onSearchContainer={handleSearchContainer}
          onSelectContainer={handleSelectContainer}
          previousItemFormValues={previousItemFormValues}
        />
        <ReportIncidentDialog
          open={openReportIncident}
          setOpen={setOpenReportIncident}
          containerId={getContainerId(currentContainer())}
          inventoryId={currentInventoryData.id}
          inventoryName={currentInventoryData.name}
        />
      </Box>
    );
  }

  return null;
}
